/* Accessible, delegated tooltips for the icons-with-labels section. Tooltip
   panels are rendered outside the slide list and moved to <body>, so Splide
   clones never duplicate their ids and overflow cannot clip them. */
if (!window.Eurus.loadedScript.has('icons-with-labels-tooltip.js')) {
  window.Eurus.loadedScript.add('icons-with-labels-tooltip.js');

  (() => {
    const WRAPPER_SELECTOR = '[data-icon-tooltip]';
    const PANEL_SELECTOR = '[data-icon-tooltip-panel]';
    const OPEN_DELAY = 40;
    const CLOSE_DELAY = 140;
    const TRANSITION_MS = 180;
    const GAP = 12;
    const EDGE = 8;
    const CARET_EDGE = 10;

    const panels = new Map();
    let active = null;
    let pendingWrapper = null;
    let openTimer = null;
    let hideTimer = null;
    let positionFrame = null;

    const closestWrapper = node => node?.closest?.(WRAPPER_SELECTOR) || null;

    const findInTarget = (target, selector) => {
      if (!target) return null;
      if (target.matches?.(selector)) return target;
      return target.querySelector?.(selector) || null;
    };

    const entryFor = wrapper => {
      if (!wrapper) return null;
      const trigger = wrapper.querySelector('.icon-with-label-item');
      const tip = panels.get(wrapper.dataset.tooltipId);
      if (!trigger || !tip) return null;
      return {
        wrapper,
        trigger,
        tip,
        preferredPlacement: wrapper.dataset.tooltipPlacement === 'bottom' ? 'bottom' : 'top'
      };
    };

    const clearTimers = () => {
      clearTimeout(openTimer);
      clearTimeout(hideTimer);
      openTimer = null;
      hideTimer = null;
      pendingWrapper = null;
    };

    const position = () => {
      positionFrame = null;
      if (!active || active.tip.hidden) return;

      const triggerRect = active.trigger.getBoundingClientRect();
      const tipRect = active.tip.getBoundingClientRect();
      const visualViewport = window.visualViewport;
      const viewportLeft = visualViewport?.offsetLeft || 0;
      const viewportTop = visualViewport?.offsetTop || 0;
      const viewportWidth = visualViewport?.width || document.documentElement.clientWidth;
      const viewportHeight = visualViewport?.height || window.innerHeight;
      const viewportRight = viewportLeft + viewportWidth;
      const viewportBottom = viewportTop + viewportHeight;
      const roomAbove = triggerRect.top - viewportTop - GAP - EDGE;
      const roomBelow = viewportBottom - triggerRect.bottom - GAP - EDGE;
      let placement = active.preferredPlacement;

      if (placement === 'top' && tipRect.height > roomAbove && roomBelow > roomAbove) {
        placement = 'bottom';
      } else if (placement === 'bottom' && tipRect.height > roomBelow && roomAbove > roomBelow) {
        placement = 'top';
      }

      const centeredLeft = triggerRect.left + (triggerRect.width - tipRect.width) / 2;
      const left = Math.max(viewportLeft + EDGE, Math.min(centeredLeft, viewportRight - tipRect.width - EDGE));
      const requestedTop = placement === 'top'
        ? triggerRect.top - tipRect.height - GAP
        : triggerRect.bottom + GAP;
      const top = Math.max(viewportTop + EDGE, Math.min(requestedTop, viewportBottom - tipRect.height - EDGE));
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const caretMax = Math.max(CARET_EDGE, tipRect.width - CARET_EDGE);
      const caretLeft = Math.max(CARET_EDGE, Math.min(triggerCenter - left, caretMax));

      active.tip.classList.toggle('il-tooltip--top', placement === 'top');
      active.tip.classList.toggle('il-tooltip--bottom', placement === 'bottom');
      active.tip.style.left = `${left}px`;
      active.tip.style.top = `${top}px`;
      active.tip.style.setProperty('--il-tip-caret-left', `${caretLeft}px`);
    };

    const requestPosition = () => {
      if (!active || positionFrame !== null) return;
      positionFrame = requestAnimationFrame(position);
    };

    const hide = (immediate = false) => {
      clearTimers();
      if (!active) return;

      const closing = active;
      active = null;
      closing.tip.classList.remove('is-open');
      if (immediate) {
        closing.tip.hidden = true;
        return;
      }

      hideTimer = setTimeout(() => {
        closing.tip.hidden = true;
        hideTimer = null;
      }, TRANSITION_MS);
    };

    const show = wrapper => {
      const next = entryFor(wrapper);
      if (!next) return;

      clearTimers();
      if (active && active.tip !== next.tip) hide(true);
      active = next;
      active.tip.hidden = false;
      position();
      requestAnimationFrame(() => {
        if (active?.tip === next.tip) next.tip.classList.add('is-open');
      });
    };

    const scheduleShow = wrapper => {
      clearTimers();
      pendingWrapper = wrapper;
      openTimer = setTimeout(() => show(wrapper), OPEN_DELAY);
    };

    const scheduleHide = () => {
      clearTimeout(openTimer);
      clearTimeout(hideTimer);
      openTimer = null;
      pendingWrapper = null;
      hideTimer = setTimeout(() => hide(), CLOSE_DELAY);
    };

    const registerPanels = root => {
      (root || document).querySelectorAll(PANEL_SELECTOR).forEach(panel => {
        const previous = panels.get(panel.id);
        if (previous && previous !== panel) previous.remove();
        panels.set(panel.id, panel);
        document.body.append(panel);
      });
    };

    const cleanupSection = sectionId => {
      if (!sectionId) return;
      if (active?.tip.dataset.tooltipSection === sectionId) hide(true);
      if (pendingWrapper?.closest?.(`[data-icons-with-labels-section="${sectionId}"]`)) clearTimers();

      panels.forEach((panel, id) => {
        if (panel.dataset.tooltipSection !== sectionId) return;
        panel.remove();
        panels.delete(id);
      });
    };

    document.addEventListener('pointerover', event => {
      if (active?.tip.contains(event.target)) {
        clearTimeout(hideTimer);
        hideTimer = null;
        return;
      }
      const wrapper = closestWrapper(event.target);
      if (!wrapper || event.pointerType === 'touch' || wrapper.contains(event.relatedTarget)) return;
      scheduleShow(wrapper);
    });

    document.addEventListener('pointerout', event => {
      if (active?.tip.contains(event.target)) {
        if (!active.tip.contains(event.relatedTarget) && !active.wrapper.contains(event.relatedTarget)) scheduleHide();
        return;
      }
      const wrapper = closestWrapper(event.target);
      if (!wrapper || wrapper.contains(event.relatedTarget) || active?.tip.contains(event.relatedTarget)) return;
      if (active?.wrapper === wrapper || pendingWrapper === wrapper) scheduleHide();
    });

    document.addEventListener('focusin', event => {
      const wrapper = closestWrapper(event.target);
      if (wrapper) scheduleShow(wrapper);
    });

    document.addEventListener('focusout', event => {
      const wrapper = closestWrapper(event.target);
      if (!wrapper || wrapper.contains(event.relatedTarget) || active?.tip.contains(event.relatedTarget)) return;
      if (active?.wrapper === wrapper || pendingWrapper === wrapper) scheduleHide();
    });

    document.addEventListener('click', event => {
      const icon = event.target.closest?.('.il-tooltip__icon');
      if (!icon) return;
      const wrapper = closestWrapper(icon);
      if (!wrapper) return;
      event.preventDefault();
      event.stopPropagation();
      if (active?.wrapper === wrapper && !active.tip.hidden) hide();
      else show(wrapper);
    });

    document.addEventListener('pointerdown', event => {
      if (!active) return;
      if (active.wrapper.contains(event.target) || active.tip.contains(event.target)) return;
      hide();
    }, { capture: true });

    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !active) return;
      hide();
      event.stopPropagation();
    }, { capture: true });

    window.addEventListener('resize', requestPosition);
    window.visualViewport?.addEventListener('resize', requestPosition);
    window.visualViewport?.addEventListener('scroll', requestPosition, { passive: true });
    document.addEventListener('scroll', requestPosition, { capture: true, passive: true });

    document.addEventListener('shopify:section:load', event => registerPanels(event.target));
    document.addEventListener('shopify:section:unload', event => {
      const section = findInTarget(event.target, '[data-icons-with-labels-section]');
      cleanupSection(section?.dataset.iconsWithLabelsSection);
    });
    document.addEventListener('shopify:block:select', event => {
      const wrapper = findInTarget(event.target, WRAPPER_SELECTOR);
      if (wrapper) show(wrapper);
    });
    document.addEventListener('shopify:block:deselect', event => {
      const wrapper = findInTarget(event.target, WRAPPER_SELECTOR);
      if (wrapper && active?.wrapper === wrapper) hide();
    });

    registerPanels(document);
  })();
}
